import { ILandingGeometry, IStairCalculationResult, IStairConfig } from '@stair-craft/common/util';

import { horizontalDim, verticalDim } from './dim-builders';
import {
    EColorRef,
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    FONT_MONO_FAMILY,
    IFont,
    IPaneRect,
    IPlanPrimitive,
    IProjectionOutput,
    emptyProjection,
} from './plan-primitive';
import { ITextMeasurer } from './text-measurer';

const PAD_LEFT: number = 56;
const PAD_RIGHT: number = 40;
const PAD_TOP: number = 30;
const PAD_BOTTOM: number = 30;
const MIN_INNER: number = 80;
const FIT_FACTOR: number = 0.78;
const ARROW_SIZE: number = 7;

const MARCH_FONT: IFont = { weight: 500, size: 9, family: FONT_MONO_FAMILY };

/** An arrow: a shaft line plus a filled head, both in `ink2`. */
function arrow(x1: number, y1: number, x2: number, y2: number, out: IPlanPrimitive[]): void {
    out.push({
        kind: EPrimitiveKind.Poly,
        points: [
            [x1, y1],
            [x2, y2],
        ],
        closed: false,
        stroke: EColorRef.Ink2,
        width: 1.4,
    });
    const dx: number = x2 - x1;
    const dy: number = y2 - y1;
    const len: number = Math.sqrt(dx * dx + dy * dy);
    if (len < 1) {
        return;
    }
    const ux: number = dx / len;
    const uy: number = dy / len;
    out.push({
        kind: EPrimitiveKind.Poly,
        points: [
            [x2, y2],
            [x2 - ARROW_SIZE * ux + (ARROW_SIZE / 2) * uy, y2 - ARROW_SIZE * uy - (ARROW_SIZE / 2) * ux],
            [x2 - ARROW_SIZE * ux - (ARROW_SIZE / 2) * uy, y2 - ARROW_SIZE * uy + (ARROW_SIZE / 2) * ux],
        ],
        closed: true,
        fill: EColorRef.Ink2,
    });
}

/**
 * Landing scheme (схема площадки) — the landing rectangle with flight-1 entry and
 * flight-2 exit arrows (perpendicular, oriented by turn direction) and its
 * width/depth dimensions. Pure projection in pane-local pixels.
 * 1:1 port of PlanViewComponent#drawLandingScheme.
 */
export function projectLandingScheme(
    result: IStairCalculationResult,
    config: IStairConfig,
    pane: IPaneRect,
    measurer: ITextMeasurer
): IProjectionOutput {
    const landing: ILandingGeometry | undefined = result.geometry.landings.at(0);
    if (!landing) {
        return emptyProjection();
    }
    const w: number = landing.width;
    const d: number = landing.depth;

    const innerW: number = pane.w - PAD_LEFT - PAD_RIGHT;
    const innerH: number = pane.h - PAD_TOP - PAD_BOTTOM;
    if (innerW < MIN_INNER || innerH < MIN_INNER || w <= 0 || d <= 0) {
        return emptyProjection();
    }

    const scale: number = Math.min(innerW / w, innerH / d) * FIT_FACTOR;
    const drawW: number = w * scale;
    const drawD: number = d * scale;
    const ox: number = PAD_LEFT + (innerW - drawW) / 2;
    const oy: number = PAD_TOP + (innerH - drawD) / 2;

    const primitives: IPlanPrimitive[] = [];

    // Landing rectangle.
    primitives.push({
        kind: EPrimitiveKind.Poly,
        points: [
            [ox, oy],
            [ox + drawW, oy],
            [ox + drawW, oy + drawD],
            [ox, oy + drawD],
        ],
        closed: true,
        fill: EColorRef.Landing,
        stroke: EColorRef.StepStroke,
        width: 1.2,
    });

    const cx: number = ox + drawW / 2;
    const cy: number = oy + drawD / 2;
    const isRight: boolean = config.turnDirection === 'right';

    // Flight 1 entry — arrow coming up into the landing from the bottom edge.
    arrow(cx, oy + drawD + 28, cx, oy + drawD + 4, primitives);
    primitives.push({
        kind: EPrimitiveKind.Text,
        at: [cx, oy + drawD + 38],
        text: $localize`:@@planLabelMarch1:Марш 1`,
        align: ETextAlign.Center,
        baseline: ETextBaseline.Middle,
        color: EColorRef.Ink2,
        font: MARCH_FONT,
    });

    // Flight 2 exit — arrow leaving through the side perpendicular to flight 1.
    const exitX: number = isRight ? ox + drawW + 28 : ox - 28;
    const exitStartX: number = isRight ? ox + drawW - 4 : ox + 4;
    const exitLabelX: number = isRight ? ox + drawW + 38 : ox - 38;
    arrow(exitStartX, cy, exitX, cy, primitives);
    primitives.push({
        kind: EPrimitiveKind.Text,
        at: [exitLabelX, cy],
        text: $localize`:@@planLabelMarch2:Марш 2`,
        align: ETextAlign.Center,
        baseline: ETextBaseline.Middle,
        color: EColorRef.Ink2,
        font: MARCH_FONT,
        rotation: -Math.PI / 2,
    });

    // Dimensions: width on top, depth on left.
    primitives.push(horizontalDim(ox, oy - 14, ox + drawW, String(Math.round(w)), measurer));
    primitives.push(verticalDim(ox - 14, oy, oy + drawD, String(Math.round(d)), measurer));

    // Millimetre origin: the landing's near-left corner; its depth grows down the screen.
    return { primitives, pxPerMm: scale, origin: { x: ox, y: oy } };
}
